/**
 * Job Detail Modal
 *
 * Displays complete job information in a modal dialog.
 * PURELY PRESENTATIONAL - all data comes from hooks.
 *
 * DDD Compliance:
 * - Uses useJobDetail hook for data
 * - No business logic in component
 * - Displays view model from application layer
 * - Handles loading and error states
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useJob, useCompleteJob } from '@/application/service-provider/hooks/useJobData';
import type { JobCardViewModel } from '@/application/service-provider/view-models/JobListViewModel';
import { CancelJobModal } from './CancelJobModal';
import { ActivityPanel } from './ActivityPanel';
import { cn } from '@/lib/utils';

interface JobDetailModalProps {
  jobId: string;
  onClose: () => void;
}

export function JobDetailModal({ jobId, onClose }: JobDetailModalProps) {
  const { data, isLoading, error } = useJob(jobId);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {isLoading && <LoadingState />}
        {error && <ErrorState message={error.message} onClose={onClose} />}
        {data && <JobDetailContent job={data} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

type TabId = 'details' | 'activity';

interface Tab {
  id: TabId;
  label: string;
}

const tabs: Tab[] = [
  { id: 'details', label: 'Details' },
  { id: 'activity', label: 'Activity' },
];

function JobDetailContent({
  job,
  onClose,
}: {
  job: JobCardViewModel;
  onClose: () => void;
}) {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('details');

  // Complete job mutation
  const completeJob = useCompleteJob();

  // Handle complete job
  const handleComplete = async () => {
    try {
      await completeJob.mutateAsync({ jobId: job.id });
      onClose(); // Close modal after successful completion
    } catch (error) {
      // Error toast is already shown by mutation hook
    }
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <DialogTitle className="text-2xl">{job.displayName}</DialogTitle>
            <DialogDescription className="text-base mt-1">
              {job.displayAddress}
            </DialogDescription>
          </div>
          <StatusBadge badge={job.statusBadge} />
        </div>
      </DialogHeader>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mt-6">
        <nav className="-mb-px flex space-x-8" aria-label="Job detail tabs">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors',
                  isActive
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
                aria-current={isActive ? 'page' : undefined}
                role="tab"
                aria-selected={isActive}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6 mt-6" role="tabpanel">
        {activeTab === 'details' && (
          <DetailsTabContent job={job} />
        )}
        {activeTab === 'activity' && (
          <ActivityPanel jobId={job.id} />
        )}
      </div>

      <DialogFooter className="mt-6 gap-2">
        {job.canComplete && (
          <button
            className="btn-primary"
            onClick={handleComplete}
            disabled={completeJob.isPending}
          >
            {completeJob.isPending ? 'Completing...' : 'Mark Complete'}
          </button>
        )}
        {job.canCancel && (
          <button
            className="btn-outline text-red-600 hover:bg-red-50"
            onClick={() => setShowCancelModal(true)}
          >
            Cancel Job
          </button>
        )}
        <button className="btn-secondary" onClick={onClose}>
          Close
        </button>
      </DialogFooter>

      {/* Cancel Modal */}
      {showCancelModal && (
        <CancelJobModal
          jobId={job.id}
          jobName={job.displayName}
          onClose={() => {
            setShowCancelModal(false);
            onClose(); // Close detail modal after cancel
          }}
        />
      )}
    </>
  );
}

// Details Tab Content (extracted from main content)
function DetailsTabContent({ job }: { job: JobCardViewModel }) {
  return (
    <>
      {/* Timeline Section */}
      <Section title="Timeline">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoField label="Schedule" value={job.scheduleText} />
          {job.targetCompletionDate && (
            <InfoField
              label="Target Completion"
              value={job.targetCompletionDate}
            />
          )}
          {job.daysRemaining !== undefined && (
            <InfoField
              label="Days Remaining"
              value={`${job.daysRemaining} days`}
              highlight={job.daysRemaining < 0 ? 'red' : undefined}
            />
          )}
        </div>

        {job.urgencyLevel === 'urgent' && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm font-medium text-red-800">
              This job is overdue and requires immediate attention.
            </p>
          </div>
        )}
      </Section>

      {/* Service Information */}
      <Section title="Service Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoField label="Service Type" value={job.serviceType} />
          <InfoField label="Container Size" value={job.containerSize} />
          <InfoField label="Material Type" value={job.materialType} />
          <InfoField label="Source" value={job.source === 'marketplace' ? 'Marketplace' : 'Direct'} />
        </div>
      </Section>

      {/* Resource Assignments */}
      {(job.assignedContainer || job.assignedVehicle || job.assignedDriver) && (
        <Section title="Assigned Resources">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {job.assignedContainer && (
              <InfoField label="Container" value={job.assignedContainer.displayName} />
            )}
            {job.assignedVehicle && (
              <InfoField label="Vehicle" value={job.assignedVehicle.displayName} />
            )}
            {job.assignedDriver && (
              <InfoField label="Driver" value={job.assignedDriver.name} />
            )}
          </div>
        </Section>
      )}

      {/* Address */}
      <Section title="Location">
        <div className="grid grid-cols-1 gap-4">
          <InfoField label="Address" value={job.displayAddress} fullWidth />
        </div>
      </Section>
    </>
  );
}

// Helper Components

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-neutral-900 mb-3 pb-2 border-b border-neutral-200">
        {title}
      </h3>
      {children}
    </div>
  );
}

function InfoField({
  label,
  value,
  fullWidth = false,
  highlight,
}: {
  label: string;
  value: string | undefined;
  fullWidth?: boolean;
  highlight?: 'red' | 'yellow' | 'green';
}) {
  const highlightClasses = {
    red: 'text-red-700 font-semibold',
    yellow: 'text-yellow-700 font-semibold',
    green: 'text-green-700 font-semibold',
  };

  const displayValue = value ?? 'Data pending';

  return (
    <div className={fullWidth ? 'md:col-span-2' : ''}>
      <dt className="text-sm font-medium text-neutral-600 mb-1">{label}</dt>
      <dd
        className={`text-sm ${value ? 'text-neutral-900' : 'text-neutral-400 italic'} ${highlight ? highlightClasses[highlight] : ''}`}
      >
        {displayValue}
      </dd>
    </div>
  );
}

function StatusBadge({
  badge,
}: {
  badge: { label: string; color: string; icon: string };
}) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-800',
  }[badge.color] || 'bg-gray-100 text-gray-800';

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-medium ${colorClasses}`}
    >
      {badge.label}
    </span>
  );
}

function LoadingState() {
  return (
    <div className="space-y-6 animate-pulse">
      <div>
        <div className="h-8 bg-neutral-200 rounded w-3/4 mb-2"></div>
        <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
      </div>
      {[1, 2, 3, 4].map((i) => (
        <div key={i}>
          <div className="h-6 bg-neutral-200 rounded w-32 mb-3"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-12 bg-neutral-100 rounded"></div>
            <div className="h-12 bg-neutral-100 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div className="text-center py-8">
      <div className="text-red-600 text-4xl mb-4">⚠️</div>
      <h3 className="text-lg font-medium text-neutral-900 mb-2">
        Unable to Load Job Details
      </h3>
      <p className="text-sm text-neutral-600 mb-4">{message}</p>
      <button className="btn-primary" onClick={onClose}>
        Close
      </button>
    </div>
  );
}
